// File: src/components/DriverManagement.js
// Driver CRUD component.
//
// A driver is one PERSON per series, and this manages one CLASS's roster of
// them. That split runs through the whole file: name, club and birth year
// belong to the person and reach every class they race, while the car number
// belongs to the roster row - the same person is legitimately #7 here and #12
// in the class next door.
//
// It is also why there are two ways to remove somebody. Taking them off this
// roster leaves them racing the other classes they entered; deleting the person
// ends their season everywhere and takes their results with it.

import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Copy, CheckSquare, Square, UserMinus } from 'lucide-react';
import { API_BASE_URL } from '../App';
import { useLanguage } from '../contexts/LanguageContext';
import { handleUnauthorized } from '../utils/session';

function DriverManagement({ currentSeries, currentClass, token, loadSeries }) {
  const { t } = useLanguage();
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [driverForm, setDriverForm] = useState({ name: '', car_number: '', club: '', birth_year: '' });
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [allSeries, setAllSeries] = useState([]);
  const [sourceSeries, setSourceSeries] = useState(null);
  const [selectedSourceDrivers, setSelectedSourceDrivers] = useState([]);

  useEffect(() => {
    loadAllSeries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllSeries = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/series`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (handleUnauthorized(response)) return;
      if (response.ok) {
        const data = await response.json();
        // Every class of every series, flattened, minus the one being filled.
      setAllSeries(data.flatMap(s => (s.classes || []).map(c => ({
        ...c, seriesName: s.name
      }))).filter(c => c.id !== currentClass.id));
      }
    } catch (error) {
      console.error('Error loading series:', error);
    }
  };

  const toggleDriverSelection = (driverId) => {
    setSelectedDrivers(prev =>
      prev.includes(driverId)
        ? prev.filter(id => id !== driverId)
        : [...prev, driverId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedDrivers.length === currentClass.drivers?.length) {
      setSelectedDrivers([]);
    } else {
      setSelectedDrivers(currentClass.drivers?.map(d => d.id) || []);
    }
  };

  const deleteSelectedDrivers = async () => {
    if (selectedDrivers.length === 0) return;
    if (!window.confirm(t('deleteSelectedDrivers').replace('{count}', selectedDrivers.length))) return;

    try {
      const responses = await Promise.all(
        selectedDrivers.map(id =>
          fetch(`${API_BASE_URL}/drivers/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          })
        )
      );
      if (responses.some(handleUnauthorized)) return;
      if (responses.some(r => !r.ok)) {
        alert(t('saveFailed'));
        return;
      }
      setSelectedDrivers([]);
      loadSeries();
    } catch (error) {
      console.error('Error deleting drivers:', error);
      alert(t('connectionError'));
    }
  };

  const toggleSourceDriverSelection = (driverId) => {
    setSelectedSourceDrivers(prev =>
      prev.includes(driverId)
        ? prev.filter(id => id !== driverId)
        : [...prev, driverId]
    );
  };

  const copyDriversFromSeries = async () => {
    if (selectedSourceDrivers.length === 0) return;

    try {
      const sourceDriversToCopy = sourceSeries.drivers.filter(d =>
        selectedSourceDrivers.includes(d.id)
      );

      const responses = await Promise.all(
        sourceDriversToCopy.map(driver =>
          fetch(`${API_BASE_URL}/drivers`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              name: driver.name,
              car_number: driver.car_number,
              club: driver.club,
              birth_year: driver.birth_year,
              series_class_id: currentClass.id
            })
          })
        )
      );

      if (responses.some(handleUnauthorized)) return;
      if (responses.some(r => !r.ok)) {
        alert(t('failedToCopyDrivers'));
        return;
      }

      alert(t('driversCopiedSuccessfully').replace('{count}', selectedSourceDrivers.length));
      setShowCopyModal(false);
      setSourceSeries(null);
      setSelectedSourceDrivers([]);
      loadSeries();
    } catch (error) {
      console.error('Error copying drivers:', error);
      alert(t('failedToCopyDrivers'));
    }
  };

  const createDriver = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/drivers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...driverForm, series_class_id: currentClass.id })
      });

      if (handleUnauthorized(response)) return;

      if (response.ok) {
        loadSeries();
        setShowModal(false);
        setDriverForm({ name: '', car_number: '', club: '', birth_year: '' });
      } else {
        const data = await response.json().catch(() => ({}));
        alert(t('saveFailed') + (data.error ? `: ${data.error}` : ''));
      }
    } catch (error) {
      console.error('Error creating driver:', error);
      alert(t('connectionError'));
    }
  };

  const updateDriver = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/drivers/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        // series_class_id is what tells the server which roster row the car
        // number belongs to; the other fields are the person's.
        body: JSON.stringify({ ...driverForm, series_class_id: currentClass.id })
      });

      if (handleUnauthorized(response)) return;

      if (response.ok) {
        loadSeries();
        setShowModal(false);
        setEditingItem(null);
        setDriverForm({ name: '', car_number: '', club: '', birth_year: '' });
      } else {
        const data = await response.json().catch(() => ({}));
        alert(t('saveFailed') + (data.error ? `: ${data.error}` : ''));
      }
    } catch (error) {
      console.error('Error updating driver:', error);
      alert(t('connectionError'));
    }
  };

  // Off this roster only. They keep racing whatever else they entered, and
  // their results in those classes are untouched.
  const removeFromClass = async (id) => {
    if (!window.confirm(t('removeFromClassConfirm'))) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/series-classes/${currentClass.id}/drivers/${id}`,
        { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (handleUnauthorized(response)) return;
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        alert(data.message || data.error || t('failedToRemoveFromClass'));
        return;
      }
      loadSeries();
    } catch (error) {
      console.error('Error removing driver from class:', error);
      alert(t('connectionError'));
    }
  };

  // The whole person, every class, every result. A different act, so a
  // different button and a warning that says as much.
  const deleteDriver = async (id) => {
    if (!window.confirm(t('deleteDriverEverywhereConfirm'))) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/drivers/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (handleUnauthorized(response)) return;
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        alert(t('saveFailed') + (data.error ? `: ${data.error}` : ''));
        return;
      }
      loadSeries();
    } catch (error) {
      console.error('Error deleting driver:', error);
      alert(t('connectionError'));
    }
  };

  return (
    <>
      <div className="bg-paper rounded-lg p-6 mb-6 border border-line">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-ink">{t('drivers')}</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setShowCopyModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-semibold flex items-center gap-2"
              title={t('copyFromOtherSeries')}
            >
              <Copy className="w-5 h-5" />
              {t('copyDrivers')}
            </button>
            <button
              onClick={() => {
                setEditingItem(null);
                setDriverForm({ name: '', car_number: '', club: '', birth_year: '' });
                setShowModal(true);
              }}
              className="bg-accent hover:opacity-90 text-accent-ink px-4 py-2 rounded font-semibold flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              {t('addDriver')}
            </button>
          </div>
        </div>

        {currentClass.drivers?.length > 0 && (
          <div className="flex justify-between items-center mb-3 pb-3 border-b border-line">
            <button
              onClick={toggleSelectAll}
              className="text-blue-700 dark:text-blue-400 hover:opacity-80 flex items-center gap-2"
            >
              {selectedDrivers.length === currentClass.drivers?.length ? (
                <CheckSquare className="w-5 h-5" />
              ) : (
                <Square className="w-5 h-5" />
              )}
              {selectedDrivers.length === currentClass.drivers?.length ? t('deselectAll') : t('selectAll')}
            </button>
            {selectedDrivers.length > 0 && (
              <button
                onClick={deleteSelectedDrivers}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded font-semibold flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                {t('deleteSelected')} ({selectedDrivers.length})
              </button>
            )}
          </div>
        )}

        <div className="space-y-2">
          {currentClass.drivers?.map(driver => (
            <div key={driver.id} className="flex items-center gap-3 p-3 bg-paper-alt rounded">
              <button
                onClick={() => toggleDriverSelection(driver.id)}
                className="text-blue-700 dark:text-blue-400 hover:opacity-80"
              >
                {selectedDrivers.includes(driver.id) ? (
                  <CheckSquare className="w-5 h-5" />
                ) : (
                  <Square className="w-5 h-5" />
                )}
              </button>
              <div className="flex-1">
                <span className="text-ink font-medium">{driver.car_number && `#${driver.car_number} - `}{driver.name}</span>
                {driver.club && <div className="text-ink-muted text-sm">{driver.club}</div>}
                {driver.birth_year && <div className="text-ink-muted text-xs">{t('born')} {driver.birth_year}</div>}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setEditingItem(driver);
                    setDriverForm({
                      name: driver.name,
                      car_number: driver.car_number || '',
                      club: driver.club || '',
                      birth_year: driver.birth_year || ''
                    });
                    setShowModal(true);
                  }}
                  className="text-blue-700 dark:text-blue-400 hover:opacity-80"
                >
                  <Edit className="w-5 h-5" />
                </button>
                <button
                  onClick={() => removeFromClass(driver.id)}
                  className="text-ink-muted hover:text-ink"
                  title={t('removeFromClass')}
                >
                  <UserMinus className="w-5 h-5" />
                </button>
                <button
                  onClick={() => deleteDriver(driver.id)}
                  className="text-red-700 dark:text-red-400 hover:opacity-80"
                  title={t('deleteDriverEverywhere')}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
          {(!currentClass.drivers || currentClass.drivers.length === 0) && (
            <p className="text-ink-muted">{t('noDriversYet')}</p>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-paper rounded-lg p-6 max-w-md w-full border border-line">
            <h3 className="text-xl font-bold text-ink mb-4">
              {editingItem ? t('editDriver') : t('newDriver')}
            </h3>
            <div className="space-y-3">
              <input
                type="text"
                placeholder={t('driverName')}
                value={driverForm.name}
                onChange={(e) => setDriverForm({ ...driverForm, name: e.target.value })}
                className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
              />
              <input
                type="number"
                placeholder={t('carNumberOptional')}
                value={driverForm.car_number}
                onChange={(e) => setDriverForm({ ...driverForm, car_number: e.target.value })}
                className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
              />
              <input
                type="text"
                placeholder={t('clubOptional')}
                value={driverForm.club}
                onChange={(e) => setDriverForm({ ...driverForm, club: e.target.value })}
                className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
              />
              <input
                type="number"
                placeholder={t('birthYearOptional')}
                value={driverForm.birth_year}
                onChange={(e) => setDriverForm({ ...driverForm, birth_year: e.target.value })}
                className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
              />
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => editingItem ? updateDriver(editingItem.id) : createDriver()}
                className="flex-1 bg-accent hover:opacity-90 text-accent-ink py-2 rounded font-semibold"
              >
                {editingItem ? t('update') : t('create')}
              </button>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingItem(null);
                }}
                className="flex-1 bg-paper-alt hover:opacity-80 text-ink py-2 rounded font-semibold"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Drivers Modal */}
      {showCopyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-paper rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto border border-line">
            <h3 className="text-xl font-bold text-ink mb-4">{t('copyDriversFromOtherSeries')}</h3>

            {!sourceSeries ? (
              <div>
                <p className="text-ink-muted mb-4">{t('selectSeriesToCopyFrom')}</p>
                {allSeries.length === 0 ? (
                  <p className="text-ink-muted">{t('noOtherSeriesAvailable')}</p>
                ) : (
                  <div className="space-y-2">
                    {allSeries.map(series => (
                      <button
                        key={series.id}
                        onClick={() => setSourceSeries(series)}
                        className="w-full p-4 bg-paper-alt hover:opacity-80 rounded text-left"
                      >
                        <div className="text-ink font-medium">{series.name}</div>
                        <div className="text-ink-muted text-sm">
                          {series.drivers?.length || 0} {t('drivers').toLowerCase()}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => setShowCopyModal(false)}
                  className="mt-4 w-full bg-paper-alt hover:opacity-80 text-ink py-2 rounded font-semibold"
                >
                  {t('cancel')}
                </button>
              </div>
            ) : (
              <div>
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-500/20 border border-blue-500 rounded">
                  <div className="text-blue-800 dark:text-blue-200 text-sm">{t('copyingFrom')}:</div>
                  <div className="text-blue-800 dark:text-blue-200 font-medium">{sourceSeries.name}</div>
                </div>

                <p className="text-ink-muted mb-3">{t('selectDriversToCopy')}:</p>

                {(!sourceSeries.drivers || sourceSeries.drivers.length === 0) ? (
                  <p className="text-ink-muted mb-4">{t('noDriversInThisClass')}</p>
                ) : (
                  <div className="space-y-2 mb-4 max-h-96 overflow-y-auto">
                    {sourceSeries.drivers.map(driver => (
                      <div
                        key={driver.id}
                        onClick={() => toggleSourceDriverSelection(driver.id)}
                        className="flex items-center gap-3 p-3 bg-paper-alt hover:opacity-80 rounded cursor-pointer"
                      >
                        {selectedSourceDrivers.includes(driver.id) ? (
                          <CheckSquare className="w-5 h-5 text-blue-700 dark:text-blue-400" />
                        ) : (
                          <Square className="w-5 h-5 text-ink-muted" />
                        )}
                        <div>
                          <span className="text-ink font-medium">
                            {driver.car_number && `#${driver.car_number} - `}{driver.name}
                          </span>
                          {driver.club && <div className="text-ink-muted text-sm">{driver.club}</div>}
                          {driver.birth_year && <div className="text-ink-muted text-xs">{t('born')} {driver.birth_year}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={copyDriversFromSeries}
                    disabled={selectedSourceDrivers.length === 0}
                    className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-paper-alt disabled:text-ink-muted text-white py-2 rounded font-semibold"
                  >
                    {t('copySelected')} ({selectedSourceDrivers.length})
                  </button>
                  <button
                    onClick={() => {
                      setSourceSeries(null);
                      setSelectedSourceDrivers([]);
                    }}
                    className="bg-paper-alt hover:opacity-80 text-ink px-4 py-2 rounded font-semibold"
                  >
                    {t('back')}
                  </button>
                  <button
                    onClick={() => {
                      setShowCopyModal(false);
                      setSourceSeries(null);
                      setSelectedSourceDrivers([]);
                    }}
                    className="bg-paper-alt hover:opacity-80 text-ink px-4 py-2 rounded font-semibold"
                  >
                    {t('cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default DriverManagement;